import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { 
  Calendar, 
  Clock, 
  Users, 
  Mail, 
  ChevronRight,
  Utensils,
  Star,
  MapPin,
  Phone
} from 'lucide-react';

export default function LandingPage() {
  const { login, isAuthenticated, user } = useAuth();

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, ease: 'easeOut' }
  };

  const stagger = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-zinc-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2" data-testid="logo">
              <Utensils className="w-6 h-6 text-orange-500" strokeWidth={1.5} />
              <span className="font-bold text-xl text-zinc-900" style={{ fontFamily: 'Manrope' }}>
                L'Ange Di Vin
              </span>
            </Link>
            
            <div className="flex items-center gap-4">
              {isAuthenticated ? (
                <Link to="/admin">
                  <Button 
                    variant="ghost" 
                    className="text-zinc-600 hover:text-zinc-900"
                    data-testid="admin-link"
                  >
                    Dashboard
                  </Button>
                </Link>
              ) : (
                <Button 
                  variant="ghost" 
                  onClick={login}
                  className="text-zinc-600 hover:text-zinc-900"
                  data-testid="login-button"
                >
                  Admin
                </Button>
              )}
              <Link to="/booking">
                <Button 
                  className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-full px-6 h-10"
                  data-testid="reserve-button-nav"
                >
                  Réserver
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-50 to-white" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div 
            className="max-w-3xl"
            initial="initial"
            animate="animate"
            variants={stagger}
          >
            <motion.p 
              variants={fadeInUp}
              className="text-sm font-medium text-orange-500 uppercase tracking-widest mb-4"
            >
              Réservation en ligne
            </motion.p>
            <motion.h1 
              variants={fadeInUp}
              className="text-5xl md:text-7xl font-bold text-zinc-900 tracking-tight leading-none mb-6"
              style={{ fontFamily: 'Manrope' }}
            >
              Votre table vous attend
            </motion.h1>
            <motion.p 
              variants={fadeInUp}
              className="text-lg md:text-xl text-zinc-600 leading-relaxed mb-10 max-w-xl"
            >
              Réservez en quelques clics et profitez d'une expérience culinaire exceptionnelle. 
              Simple, rapide et sans engagement.
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-wrap gap-4">
              <Link to="/booking">
                <Button 
                  size="lg"
                  className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-full h-14 px-8 text-base font-medium transition-all active:scale-95"
                  data-testid="reserve-button-hero"
                >
                  Réserver une table
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
        
        {/* Hero Image */}
        <motion.div 
          className="mt-16 md:mt-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <div className="relative rounded-2xl overflow-hidden aspect-[16/7]">
            <img 
              src="https://customer-assets.emergentagent.com/job_reserve-app-3/artifacts/p8qqhvz4_IMG_7570.jpeg"
              alt="L'Ange Di Vin"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-zinc-900/40 to-transparent" />
          </div>
        </motion.div>
      </section>

      {/* Features Section */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-sm font-medium text-orange-500 uppercase tracking-widest mb-4">
              Comment ça marche
            </p>
            <h2 
              className="text-4xl md:text-5xl font-semibold text-zinc-900 tracking-tight"
              style={{ fontFamily: 'Manrope' }}
            >
              Simple comme bonjour
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: Calendar,
                title: 'Choisissez la date',
                description: 'Sélectionnez la date qui vous convient dans notre calendrier interactif.'
              },
              {
                icon: Clock,
                title: 'Sélectionnez l\'heure',
                description: 'Consultez les créneaux disponibles et choisissez celui qui vous arrange.'
              },
              {
                icon: Mail,
                title: 'Confirmez par email',
                description: 'Recevez instantanément la confirmation de votre réservation.'
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                className="bg-zinc-50 border border-zinc-200 rounded-2xl p-8 hover:border-zinc-300 transition-colors"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                data-testid={`feature-card-${index}`}
              >
                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-6 shadow-sm">
                  <feature.icon className="w-6 h-6 text-orange-500" strokeWidth={1.5} />
                </div>
                <h3 
                  className="text-xl font-semibold text-zinc-900 mb-3"
                  style={{ fontFamily: 'Manrope' }}
                >
                  {feature.title}
                </h3>
                <p className="text-zinc-600 leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 md:py-32 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-sm font-medium text-orange-500 uppercase tracking-widest mb-4">
                Notre restaurant
              </p>
              <h2 
                className="text-4xl md:text-5xl font-semibold text-zinc-900 tracking-tight mb-6"
                style={{ fontFamily: 'Manrope' }}
              >
                Une expérience unique
              </h2>
              <p className="text-zinc-600 text-lg leading-relaxed mb-8">
                Niché au cœur de la ville, notre restaurant vous accueille dans un cadre 
                moderne et chaleureux. Notre chef passionné vous propose une cuisine 
                créative mettant en valeur les produits de saison.
              </p>
              
              <div className="flex flex-wrap gap-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                    <Star className="w-5 h-5 text-orange-500" fill="currentColor" />
                  </div>
                  <div>
                    <p className="font-semibold text-zinc-900">4.8/5</p>
                    <p className="text-sm text-zinc-500">Avis clients</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                    <Users className="w-5 h-5 text-orange-500" />
                  </div>
                  <div>
                    <p className="font-semibold text-zinc-900">50 places</p>
                    <p className="text-sm text-zinc-500">Capacité</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="rounded-2xl overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_reserve-app-3/artifacts/p8qqhvz4_IMG_7570.jpeg"
                  alt="L'Ange Di Vin"
                  className="w-full h-[400px] object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 
              className="text-4xl md:text-5xl font-semibold text-white tracking-tight mb-6"
              style={{ fontFamily: 'Manrope' }}
            >
              Prêt à réserver ?
            </h2>
            <p className="text-zinc-400 text-lg mb-10 max-w-xl mx-auto">
              Ne tardez plus et réservez votre table dès maintenant. 
              Notre équipe vous attend avec impatience.
            </p>
            <Link to="/booking">
              <Button 
                size="lg"
                className="bg-orange-500 text-white hover:bg-orange-600 rounded-full h-14 px-10 text-base font-medium transition-all active:scale-95"
                data-testid="reserve-button-cta"
              >
                Réserver maintenant
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-zinc-950 border-t border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <Utensils className="w-5 h-5 text-orange-500" strokeWidth={1.5} />
              <span className="font-bold text-lg text-white" style={{ fontFamily: 'Manrope' }}>
                L'Ange Di Vin
              </span>
            </div>
            
            <div className="flex items-center gap-6 text-zinc-400 text-sm">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                <span>13 rue de la Monnaie, Lyon 2</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>18h - 1h</span>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4" />
                <a href="tel:+33745198222" className="hover:text-white transition-colors">07 45 19 82 22</a>
              </div>
            </div>

            <p className="text-zinc-500 text-sm">
              © 2025 L'Ange Di Vin. Tous droits réservés.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
